import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Check, Close, Delete } from '@mui/icons-material';
import ModerationService, { PendingItems } from '../../services/moderation-service';
import { BlogArticle, BlogComment } from '../../types/blog';

const ModerationDashboard: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState<PendingItems>({ articles: [], comments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; articleId: string }>({ open: false, articleId: '' });
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await ModerationService.getPendingItems();
      setData(items);
    } catch {
      setError('Failed to load moderation queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (articleId: string) => {
    await ModerationService.approveArticle(articleId);
    await fetchData();
  };

  const handleReject = async () => {
    await ModerationService.rejectArticle(rejectDialog.articleId, rejectReason);
    setRejectDialog({ open: false, articleId: '' });
    setRejectReason('');
    await fetchData();
  };

  const handleDeleteComment = async (commentId: string) => {
    await ModerationService.deleteComment(commentId);
    await fetchData();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Content Moderation</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Pending Articles (${data.articles.length})`} />
        <Tab label={`Flagged Comments (${data.comments.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.articles.map((a: BlogArticle) => (
                <TableRow key={a.articleId}>
                  <TableCell>{a.title}</TableCell>
                  <TableCell>{a.authorName}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell><Chip label={a.status} size="small" color="warning" /></TableCell>
                  <TableCell>{new Date(a.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Check />} color="success" onClick={() => handleApprove(a.articleId)}>Approve</Button>
                    <Button size="small" startIcon={<Close />} color="error" onClick={() => setRejectDialog({ open: true, articleId: a.articleId })}>Reject</Button>
                  </TableCell>
                </TableRow>
              ))}
              {data.articles.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No pending articles.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Comment</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.comments.map((c: BlogComment) => (
                <TableRow key={c.commentId}>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.body}</TableCell>
                  <TableCell>{c.authorName}</TableCell>
                  <TableCell><Chip label={c.status} size="small" color="error" /></TableCell>
                  <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Delete />} color="error" onClick={() => handleDeleteComment(c.commentId)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
              {data.comments.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">No flagged comments.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, articleId: '' })}>
        <DialogTitle>Reject Article</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={3} label="Rejection Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, articleId: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectReason.trim()}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModerationDashboard;
